import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, ArrowLeft, Shield, Eye, Lock, Server, Users, Mail } from 'lucide-react';

const PrivacyPage = () => {
  const lastUpdated = "March 20, 2026";
  const effectiveDate = "March 20, 2026";

  return (
    <div className="min-h-screen bg-paper">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-ink-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <CalendarCheck className="w-7 h-7 text-ink" />
              <span className="text-xl font-bold text-ink font-display">Enota</span>
            </Link>
            <Link to="/" className="text-ink-600 hover:text-ink flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-md border border-ink-200 shadow-sm p-8 md:p-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-ink font-display mb-2">
              Privacy Policy
            </h1>
            <p className="text-ink-600 mb-2">Last Updated: {lastUpdated}</p>
            <p className="text-ink-600 mb-8">Effective Date: {effectiveDate}</p>

            {/* Quick Summary */}
            <div className="bg-cream border border-ink-200 rounded-md p-6 mb-8">
              <h2 className="text-lg font-semibold text-ink mb-4">Privacy at a Glance</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-respondent mt-0.5" />
                  <div>
                    <p className="font-medium text-ink">Your Data, Your Control</p>
                    <p className="text-sm text-respondent">Export or delete your data anytime</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-respondent mt-0.5" />
                  <div>
                    <p className="font-medium text-ink">Encrypted & Secure</p>
                    <p className="text-sm text-respondent">Industry-standard encryption</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Eye className="w-5 h-5 text-respondent mt-0.5" />
                  <div>
                    <p className="font-medium text-ink">No Data Selling</p>
                    <p className="text-sm text-respondent">We never sell your personal data</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Server className="w-5 h-5 text-respondent mt-0.5" />
                  <div>
                    <p className="font-medium text-ink">Minimal Collection</p>
                    <p className="text-sm text-respondent">Only what we need to serve you</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              <p className="text-ink-600 leading-relaxed mb-6">
                Enota, a service owned and operated by <strong>OFFER GADGET LLC</strong> ("we," "us," or "our"), 
                is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, 
                and safeguard your information when you use our website, mobile application, and services 
                (collectively, the "Service"). Please read this policy carefully to understand our practices 
                regarding your personal data.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                1. Information We Collect
              </h2>
              
              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">
                1.1 Information You Provide Directly
              </h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                We collect information you voluntarily provide when using our Service:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li><strong>Account Information:</strong> Name, email address, password (hashed), phone number (optional), and profile picture when you register or update your account</li>
                <li><strong>Renewal Data:</strong> Information about your renewals, including titles, categories, dates, descriptions, notes, and reminder preferences</li>
                <li><strong>Payment Information:</strong> When you subscribe to a paid plan, our payment processor (Stripe) collects billing information. We do not store complete credit card numbers on our servers</li>
                <li><strong>Communications:</strong> Information you provide when contacting our support team, including email content and attachments</li>
                <li><strong>Feedback:</strong> Reviews, ratings, and feedback you provide about our Service</li>
              </ul>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">
                1.2 Information Collected Automatically
              </h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                When you access or use our Service, we automatically collect certain information:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li><strong>Device Information:</strong> Browser type, operating system, device type, unique device identifiers, and mobile network information</li>
                <li><strong>Log Data:</strong> IP address, access times, pages viewed, referring URL, and the page you visited before navigating to our Service</li>
                <li><strong>Usage Data:</strong> Features used, actions taken, time spent on pages, and interaction patterns</li>
                <li><strong>Location Data:</strong> General geographic location based on IP address (city/country level only)</li>
                <li><strong>Cookies and Similar Technologies:</strong> We use cookies, web beacons, and similar technologies to collect information about your browsing activities</li>
              </ul>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">
                1.3 Information from Third Parties
              </h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                We may receive information from third-party sources:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li><strong>Social Login Providers:</strong> If you sign in using Google, we receive your name, email, and profile picture from Google</li>
                <li><strong>Payment Processors:</strong> Transaction status, last four digits of card, and billing address from Stripe</li>
                <li><strong>Analytics Providers:</strong> Aggregated usage statistics and performance metrics</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                2. How We Use Your Information
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We use the collected information for the following purposes:
              </p>
              
              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">2.1 Providing and Improving Our Service</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Creating and managing your account</li>
                <li>Storing and displaying your renewal information</li>
                <li>Sending renewal reminders via email and SMS</li>
                <li>Processing payments and managing subscriptions</li>
                <li>Providing customer support and responding to inquiries</li>
                <li>Analyzing usage patterns to improve features and user experience</li>
                <li>Developing new products, services, and features</li>
              </ul>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">2.2 Communication</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Sending transactional emails (account verification, password resets, renewal reminders)</li>
                <li>Sending service announcements and updates</li>
                <li>Marketing communications (with your consent, and you can opt out anytime)</li>
                <li>Responding to your comments, questions, and requests</li>
              </ul>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">2.3 Security and Legal Compliance</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Detecting, preventing, and addressing fraud, security issues, and technical problems</li>
                <li>Enforcing our Terms of Use and other policies</li>
                <li>Complying with legal obligations and responding to lawful requests</li>
                <li>Protecting the rights, property, and safety of Enota and our users</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                3. How We Share Your Information
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We do not sell your personal information. We may share your information in the following circumstances:
              </p>
              
              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">3.1 Service Providers</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                We share information with third-party service providers who perform services on our behalf:
              </p>
              <div className="bg-paper p-4 rounded-md border border-ink-200 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-ink-200">
                      <th className="text-left py-2 text-ink">Provider</th>
                      <th className="text-left py-2 text-ink">Purpose</th>
                      <th className="text-left py-2 text-ink">Data Shared</th>
                    </tr>
                  </thead>
                  <tbody className="text-ink-600">
                    <tr className="border-b border-ink-200">
                      <td className="py-2">Stripe</td>
                      <td className="py-2">Payment processing</td>
                      <td className="py-2">Billing information</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-2">Resend</td>
                      <td className="py-2">Email delivery</td>
                      <td className="py-2">Email address, name</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-2">Twilio</td>
                      <td className="py-2">SMS delivery</td>
                      <td className="py-2">Phone number</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-2">MongoDB Atlas</td>
                      <td className="py-2">Data storage</td>
                      <td className="py-2">All user data (encrypted)</td>
                    </tr>
                    <tr>
                      <td className="py-2">Google OAuth</td>
                      <td className="py-2">Authentication</td>
                      <td className="py-2">Email, name, profile picture</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">3.2 Legal Requirements</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                We may disclose your information if required by law or in response to valid legal requests 
                by public authorities (e.g., court orders, subpoenas, government requests).
              </p>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">3.3 Business Transfers</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                In the event of a merger, acquisition, reorganization, or sale of assets, your information 
                may be transferred as part of the transaction. We will notify you of any such change and 
                the choices you may have regarding your information.
              </p>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">3.4 With Your Consent</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                We may share your information for other purposes with your explicit consent.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                4. Data Security
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
                <li><strong>Password Security:</strong> Passwords are hashed using bcrypt with salt</li>
                <li><strong>Access Controls:</strong> Strict access controls and authentication for all systems</li>
                <li><strong>Regular Audits:</strong> Periodic security assessments and vulnerability testing</li>
                <li><strong>Incident Response:</strong> Established procedures for detecting and responding to security incidents</li>
              </ul>
              <p className="text-ink-600 leading-relaxed mb-4">
                However, no method of transmission over the Internet or electronic storage is 100% secure. 
                While we strive to protect your personal information, we cannot guarantee its absolute security.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                5. Data Retention
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We retain your personal information for as long as necessary to fulfill the purposes outlined 
                in this Privacy Policy, unless a longer retention period is required or permitted by law:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li><strong>Active Accounts:</strong> Data retained while your account is active</li>
                <li><strong>Deleted Accounts:</strong> Data deleted within 30 days of account deletion request</li>
                <li><strong>Backup Retention:</strong> Encrypted backups retained for up to 90 days</li>
                <li><strong>Legal Holds:</strong> Data may be retained longer if required for legal proceedings</li>
                <li><strong>Anonymized Data:</strong> Aggregated, anonymized data may be retained indefinitely</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                6. Your Privacy Rights
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              
              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">6.1 Access and Portability</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                You have the right to request a copy of your personal information in a structured, 
                machine-readable format. You can export your data directly from your account settings.
              </p>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">6.2 Correction</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                You can update or correct your personal information through your account settings or by 
                contacting our support team.
              </p>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">6.3 Deletion</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                You have the right to request deletion of your personal information. You can delete your 
                account through settings, and we will erase your data within 30 days, except where retention 
                is required by law.
              </p>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">6.4 Objection and Restriction</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                You may object to certain processing activities or request restriction of processing in 
                specific circumstances.
              </p>

              <h3 className="text-lg font-semibold text-ink mt-6 mb-3">6.5 Marketing Opt-Out</h3>
              <p className="text-ink-600 leading-relaxed mb-4">
                You can opt out of marketing communications at any time by clicking "unsubscribe" in our 
                emails or updating your notification preferences in settings.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                7. Cookies and Tracking Technologies
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We use cookies and similar technologies to enhance your experience:
              </p>
              <div className="bg-paper p-4 rounded-md border border-ink-200 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-ink-200">
                      <th className="text-left py-2 text-ink">Cookie Type</th>
                      <th className="text-left py-2 text-ink">Purpose</th>
                      <th className="text-left py-2 text-ink">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-ink-600">
                    <tr className="border-b border-ink-200">
                      <td className="py-2">Essential</td>
                      <td className="py-2">Authentication, security</td>
                      <td className="py-2">Session / 7 days</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-2">Functional</td>
                      <td className="py-2">Preferences, settings</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="py-2">Analytics</td>
                      <td className="py-2">Usage statistics</td>
                      <td className="py-2">2 years</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-ink-600 leading-relaxed mb-4">
                You can control cookies through your browser settings. Note that disabling certain cookies 
                may affect the functionality of our Service.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                8. International Data Transfers
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                Your information may be transferred to and processed in countries other than your country 
                of residence. These countries may have different data protection laws. When we transfer 
                data internationally, we implement appropriate safeguards, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Standard Contractual Clauses approved by regulatory authorities</li>
                <li>Data Processing Agreements with all service providers</li>
                <li>Compliance with applicable cross-border data transfer regulations</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                9. Children's Privacy
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                Our Service is not intended for children under 18 years of age. We do not knowingly collect 
                personal information from children under 18. If you are a parent or guardian and believe 
                your child has provided us with personal information, please contact us immediately. If we 
                discover that a child under 18 has provided us with personal information, we will delete 
                such information promptly.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                10. California Privacy Rights (CCPA)
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                If you are a California resident, you have additional rights under the California Consumer 
                Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li><strong>Right to Know:</strong> Request disclosure of the categories and specific pieces of personal information we collect</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights</li>
                <li><strong>Right to Opt-Out:</strong> We do not sell personal information, so the right to opt-out of sale does not apply</li>
              </ul>
              <p className="text-ink-600 leading-relaxed mb-4">
                To exercise these rights, contact us at support@renewalnote.com. We will verify your identity 
                before processing your request.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                11. European Privacy Rights (GDPR)
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you 
                have the following additional rights under the General Data Protection Regulation (GDPR):
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li><strong>Legal Basis:</strong> We process your data based on consent, contract performance, legitimate interests, or legal obligations</li>
                <li><strong>Data Protection Officer:</strong> You may contact our DPO at support@renewalnote.com</li>
                <li><strong>Supervisory Authority:</strong> You have the right to lodge a complaint with your local data protection authority</li>
                <li><strong>Automated Decisions:</strong> We do not make solely automated decisions that significantly affect you</li>
              </ul>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                12. Changes to This Privacy Policy
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any material 
                changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. 
                For significant changes, we will also send you an email notification.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                We encourage you to review this Privacy Policy periodically for any changes. Your continued 
                use of the Service after any modifications indicates your acceptance of the updated Privacy Policy.
              </p>

              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4">
                13. Contact Us
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data 
                practices, please contact us:
              </p>
              <div className="bg-paper p-4 rounded-md border border-ink-200 mb-4">
                <div className="flex items-start gap-3 mb-3">
                  <Mail className="w-5 h-5 text-ink-600 mt-0.5" />
                  <div>
                    <p className="text-ink font-medium">Privacy Team</p>
                    <p className="text-ink-600">support@renewalnote.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <Users className="w-5 h-5 text-ink-600 mt-0.5" />
                  <div>
                    <p className="text-ink font-medium">Data Protection Officer</p>
                    <p className="text-ink-600">support@renewalnote.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Server className="w-5 h-5 text-ink-600 mt-0.5" />
                  <div>
                    <p className="text-ink font-medium">Mailing Address</p>
                    <p className="text-ink-600"><strong>OFFER GADGET LLC</strong><br />53 Calle Palmeras Ste 601<br />San Juan, PR 00901</p>
                  </div>
                </div>
              </div>
              <p className="text-ink-600 leading-relaxed mb-4">
                We aim to respond to all privacy-related inquiries within 30 days.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-ink text-ink-600/70 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-6 h-6 text-white" />
              <span className="text-lg font-bold text-white font-display">Enota</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/california-privacy" className="hover:text-white transition-colors">California Residents</Link>
              <Link to="/login" className="hover:text-white transition-colors">Login</Link>
            </div>
            <p className="text-sm">&copy; 2026 Enota. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
